/* oxlint-disable max-classes-per-file */
import { configureStore } from '@reduxjs/toolkit';
import { frontRoyalStoreApi } from 'FrontRoyalStore';
import { cohortsApi } from 'Cohorts';
// Import API slices and Redux slices directly from their source files rather than
// through barrel (index.ts) exports. Barrel imports defeat tree-shaking when the
// barrel also re-exports UI components, causing admin-only code to be bundled
// into the front-royal entry point loaded by all users.
import { chatSlice } from 'TutorBotChat/redux/chat/chatSlice';
import { themeSlice } from 'Theme';
import { storeProvider, combineApisAndSlices } from 'ReduxHelpers';
import { autoSuggestOptionsApi } from 'AutoSuggestOptions';
import { batchEditUsersApi } from 'BatchEditUsers/redux/api';
import { careerProfilesApi } from 'CareerProfiles/CareerProfilesApi';
import { lessonsApi } from 'Lessons/LessonsApi';
import { lessonsEditorApi } from 'Lessons/LessonsEditorApi';
import { playlistExamEvaluationsApi } from 'PlaylistExamEvaluation/PlaylistExamEvaluationsApi';
import { resourcesApi } from 'Resources/resourcesApi';
import { activityTimelineApi } from 'ActivityTimelineTab/redux/api';
import { editAoiRecordsApi } from 'UserAdministrationTab/redux/api';
import { adminStudentRecordsApi } from 'AdminStudentRecordsTab/redux/api';
import { adminLessonPermissionsApi } from 'AdminLessonPermissionsTab/redux/api';
import { globalMetadataApi } from 'AdminGlobalMetadata/globalMetadataApi';
import { playlistsApi } from 'Playlists/PlaylistsApi';
import { playlistEditorApi } from 'PlaylistEditor/playlistEditorApi';
import { entityMetadataApi } from 'ContentItemEditor/entityMetadataApi';
import { streamEditorApi } from 'StreamEditor/streamEditorApi';
import { messagingSlice } from 'Messaging/messagingSlice';
import { peopleFiltersSlice } from 'StudentNetworkV2/peopleFiltersSlice';
import { studentNetworkApi } from 'StudentNetwork/StudentNetworkApi';
import { liveKitApi } from 'TutorBotVoiceAgent/livekitApi';
import { voiceAgentSlice } from 'TutorBotVoiceAgent/redux/voiceAgent/voiceAgentSlice';
import { studentDashboardSlice } from 'StudentDashboardSlice';
import { projectGroupsApi } from 'ProjectGroup/projectGroupsApi';
import { projectGroupMembershipsApi } from 'ProjectGroup/projectGroupMembershipsApi';
import { uploadProjectSubmissionApi } from 'ProjectSubmission/uploadProjectSubmissionApi';
import { userActionsApi } from 'Users/UserActionsApi';
import { ghostModeSlice } from 'Users/ghostModeSlice';
import { routeTransitionSlice } from 'RouteAnimation';
import { navigationSlice } from 'Navigation/navigationSlice';
import { breadcrumbNavigationSlice } from 'BreadcrumbNavigation';
// Also avoids loading AppHeaderReact components/hooks whose Angular dependencies
// cause issues in test environments.
import { appHeaderSlice } from 'AppHeaderReact/redux';
import { appHeaderSynchronizer } from 'AppHeaderReact/sync';
import { gradingPortalApi } from 'GradingPortal/api';
import { lessonGenerationPortalApi } from 'LessonGenerationPortal/api/lessonGenerationPortalApi';
import { studentNetworkMapsApi } from 'StudentNetworkV2/api/studentNetworkMapsApi';
import { studentNetworkProfilesApi } from 'StudentNetworkV2/api/studentNetworkProfilesApi';
import { studentNetworkEventsV2Api } from 'StudentNetworkV2/api/studentNetworkEventsV2Api';
import { autogradingTestWizardApi } from 'AutogradingTestWizard/api/autogradingTestWizardApi';
import { billingTransactionsApi } from 'BillingTransactionsTab/redux/api';
import { adminEnrollmentStatusApi } from 'AdminCohortEnrollmentStatus/adminEnrollmentStatusApi';
import { adminProjectGroupsApi } from 'AdminCohortProjectGroups/adminProjectGroupsApi';
import { customMiddleware } from './middleware';

const apis = [
    activityTimelineApi,
    adminEnrollmentStatusApi,
    adminLessonPermissionsApi,
    adminProjectGroupsApi,
    adminStudentRecordsApi,
    autogradingTestWizardApi,
    autoSuggestOptionsApi,
    batchEditUsersApi,
    billingTransactionsApi,
    careerProfilesApi,
    cohortsApi,
    gradingPortalApi,
    lessonGenerationPortalApi,
    projectGroupsApi,
    projectGroupMembershipsApi,
    editAoiRecordsApi,
    frontRoyalStoreApi,
    lessonsApi,
    lessonsEditorApi,
    playlistsApi,
    playlistEditorApi,
    playlistExamEvaluationsApi,
    entityMetadataApi,
    globalMetadataApi,
    streamEditorApi,
    resourcesApi,
    liveKitApi,
    studentNetworkApi,
    uploadProjectSubmissionApi,
    userActionsApi,
    studentNetworkMapsApi,
    studentNetworkProfilesApi,
    studentNetworkEventsV2Api,
];

const slices = [
    appHeaderSlice,
    chatSlice,
    themeSlice,
    messagingSlice,
    voiceAgentSlice,
    studentDashboardSlice,
    routeTransitionSlice,
    navigationSlice,
    breadcrumbNavigationSlice,
    peopleFiltersSlice,
    ghostModeSlice,
];
export function initializeAppStore() {
    if (storeProvider.store) {
        throw new Error('store is already defined');
    }

    const { reducer, middleware } = combineApisAndSlices({
        apis,
        slices,
        middleware: customMiddleware,
    });

    const store = configureStore({
        reducer,
        middleware,
    });

    storeProvider.store = store;

    // Initialize the AppHeader synchronizer with the store for Angular-Redux state sync
    appHeaderSynchronizer.initializeStore(store);

    return store;
}

// In lesson preview mode, the storeProvider is attached to the iframe.contentWindow
export const getState = () => (window.storeProvider ?? storeProvider).store!.getState();
